import { useCallback, useRef } from 'react';

import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientClientResponseSummaryModel } from 'src/api/generated';
import ScrollGradient from 'src/components/ScrollGradient';
import { useHandleLongPress } from 'src/hooks/useLongPress';

import {
  EditButton,
  MobileEmail,
  MobileRow,
  RowActions,
  RowActionsWrapper,
  Wrapper,
} from '@/pages/Users/components/UsersTable/components/UsersMobileRow/UsersMobileRow.styled';

const clientRowKey = (client: FanguardApiContractsClientClientResponseSummaryModel) => client.id ?? '';

interface AdminClientsMobileNameColumnProps {
  client: FanguardApiContractsClientClientResponseSummaryModel;
  activeClientId: string | null;
  setActiveClientId: (id: string | null) => void;
}

export const AdminClientsMobileNameColumn = (props: AdminClientsMobileNameColumnProps) => {
  const { client, activeClientId, setActiveClientId } = props;
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const id = clientRowKey(client);

  const licensedForLine =
    client.licensedFor && client.licensedFor.length > 0
      ? client.licensedFor.map((key) => t(`adminClients.licensedFor.${key}`, { defaultValue: key })).join(', ')
      : '—';

  const { handlers } = useHandleLongPress({
    handleClickOutside: () => {
      setTimeout(() => setActiveClientId(null), 100);
    },
    handleSetLongPress: (context) => {
      if (context) {
        setActiveClientId(context);
      }
    },
    handleLongPressStart: () => {
      setActiveClientId(null);
    },
    ref: rowRef,
  });

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (activeClientId === id) {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }
  };

  return (
    <ScrollGradient>
      <Wrapper>
        <MobileRow
          {...handlers(id)}
          className={activeClientId === id ? 'active' : ''}
          onClick={handleRowClick}
          data-active={activeClientId === id ? 'true' : 'false'}
        >
          <Flex ref={rowRef}>
            <Flex vertical>
              <div>{client.name ?? client.id ?? '—'}</div>
              <MobileEmail>{licensedForLine}</MobileEmail>
            </Flex>
          </Flex>
        </MobileRow>
      </Wrapper>
    </ScrollGradient>
  );
};

interface AdminClientsMobileActionsColumnProps {
  client: FanguardApiContractsClientClientResponseSummaryModel;
  activeClientId: string | null;
  onEditClient: (record: FanguardApiContractsClientClientResponseSummaryModel) => void;
}

export const AdminClientsMobileActionsColumn = (props: AdminClientsMobileActionsColumnProps) => {
  const { client, activeClientId, onEditClient } = props;
  const actionsRef = useRef<HTMLDivElement>(null);
  const id = clientRowKey(client);

  const handleClickEditButton = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
      onEditClient(client);
    },
    [client, onEditClient],
  );

  if (activeClientId !== id) {
    return <Wrapper style={{ minHeight: 46, position: 'relative' }} />;
  }

  return (
    <Wrapper style={{ minHeight: 46, position: 'relative' }}>
      <RowActionsWrapper ref={actionsRef}>
        <RowActions>
          <EditButton data-ignore-outside aria-label="edit" onClick={handleClickEditButton} />
        </RowActions>
      </RowActionsWrapper>
    </Wrapper>
  );
};
